import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from '../hooks/use-i18n'
import { getMyPublicEnrollments, QUERY_KEY_MY_PUBLIC_ENROLLMENTS } from '../services/publicService'
import {
  asPublicEnrollmentList,
  findActiveFootballParticipant,
  findActiveFormula1Participant,
} from '../utils/publicFootballEnrollment'
import type { WorldEnrollExtras } from '../utils/worldEnrollCache'
import OnboardingConciergeModal from './agent/OnboardingConciergeModal'
import { Link } from 'react-router-dom'

interface PublicSectionProps {
  onRequestPlayWorld: () => void
  onWorldEnrolled: (tournamentId: string, gameType?: string, extras?: WorldEnrollExtras) => void | Promise<void>
}

const PublicSection: React.FC<PublicSectionProps> = ({ onRequestPlayWorld, onWorldEnrolled }) => {
  const { t } = useTranslation()
  const [showConciergeModal, setShowConciergeModal] = useState(false)

  const { data: enrollmentsRaw, isLoading: loading } = useQuery({
    queryKey: QUERY_KEY_MY_PUBLIC_ENROLLMENTS,
    queryFn: getMyPublicEnrollments,
    staleTime: 2 * 60 * 1000,
    retry: 1,
  })

  const enrollments = asPublicEnrollmentList(enrollmentsRaw)
  const hasFootballEnrollment = Boolean(findActiveFootballParticipant(enrollments))
  const firstFootballEnrollment = findActiveFootballParticipant(enrollments)
  const firstFormula1Enrollment = findActiveFormula1Participant(enrollments)
  const hasFormula1Enrollment = Boolean(firstFormula1Enrollment)

  // Don't block the dashboard — show world section once loaded (matches mobile).
  if (loading && enrollments.length === 0) {
    return null
  }

  return (
    <section className="py-3 sm:py-6 lg:py-8">
      <h2 className="font-display text-lg sm:text-display-2 font-bold text-foreground mb-4 sm:mb-6">
        {t('dashboard.yourPublicTournaments')}
      </h2>

      {!hasFootballEnrollment && !hasFormula1Enrollment ? (
        <div className="text-center py-6 sm:py-8 lg:py-12">
          <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-8 max-w-md mx-auto">
            <div className="text-4xl sm:text-6xl mb-4">🌍</div>
            <h3 className="font-display text-lg font-semibold mb-2 text-foreground">{t('playPublic.title')}</h3>
            <p className="text-small text-muted-foreground mb-4">
              {t('playPublic.subtitle')}
            </p>
            <button
              onClick={onRequestPlayWorld}
              className="w-full px-4 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-small hover:opacity-90 transition-all border border-sky-200/80"
            >
              🌍 {t('dashboard.playWorld')} ⚽ 🏎️
            </button>
            <button
              type="button"
              onClick={() => setShowConciergeModal(true)}
              className="w-full mt-2 px-4 py-2.5 rounded-xl border border-primary/40 text-foreground font-medium text-small hover:bg-primary/5 transition-all"
            >
              ✨ {t('agent.askConcierge')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 flex flex-col max-w-sm">
          {hasFootballEnrollment && (
            <Link
              to="/public/football"
              className="glass-card rounded-lg sm:rounded-xl p-3 sm:p-4 hover:bg-muted/10 transition-all block border-2 border-sky-200/70"
            >
              <div className="flex justify-between items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl" aria-hidden>⚽</span>
                  <div>
                    <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
                      {t('dashboard.worldFootball')}
                      <span className="text-[10px] font-extrabold tracking-wide uppercase px-1.5 py-0.5 rounded-full border border-white/50 bg-white/10">🌍 {t('dashboard.worldBadge')}</span>
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {firstFootballEnrollment?.countryName}
                      {firstFootballEnrollment?.city ? `, ${firstFootballEnrollment.city}` : ''}
                    </p>
                  </div>
                </div>
                <span className="text-primary text-xs font-medium">→</span>
              </div>
            </Link>
          )}
          {hasFormula1Enrollment && (
            <Link
              to="/public/formula1"
              className="glass-card rounded-lg sm:rounded-xl p-3 sm:p-4 hover:bg-muted/10 transition-all block border-2 border-rose-200/70"
            >
              <div className="flex justify-between items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl" aria-hidden>🏎️</span>
                  <div>
                    <h3 className="font-display text-base font-semibold text-foreground flex items-center gap-2">
                      {t('dashboard.worldFormula1')}
                      <span className="text-[10px] font-extrabold tracking-wide uppercase px-1.5 py-0.5 rounded-full border border-white/50 bg-white/10">🌍 {t('dashboard.worldBadge')}</span>
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {firstFormula1Enrollment?.countryName}
                      {firstFormula1Enrollment?.city ? `, ${firstFormula1Enrollment.city}` : ''}
                    </p>
                  </div>
                </div>
                <span className="text-primary text-xs font-medium">→</span>
              </div>
            </Link>
          )}
          {(!hasFootballEnrollment || !hasFormula1Enrollment) && (
            <button
              onClick={onRequestPlayWorld}
              className={`w-full text-left px-4 py-3 rounded-2xl font-semibold text-sm hover:opacity-95 transition-all border-2 flex items-center gap-3 ${
                hasFootballEnrollment && !hasFormula1Enrollment
                  ? 'bg-[#5c1a24] text-white border-rose-200/40'
                  : 'bg-primary text-primary-foreground border-sky-200/80'
              }`}
            >
              <span className="text-2xl leading-none" aria-hidden>
                {hasFootballEnrollment && !hasFormula1Enrollment ? '🏎️' : '⚽'}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[10px] font-extrabold tracking-wider uppercase opacity-80">🌍 {t('dashboard.joinTheWorld')}</span>
                <span className="block">
                  {hasFootballEnrollment && !hasFormula1Enrollment
                    ? t('dashboard.playWorldFormula1')
                    : t('dashboard.playWorldFootball')}
                </span>
                <span className="block text-xs font-medium opacity-90 mt-0.5">
                  {hasFootballEnrollment && !hasFormula1Enrollment
                    ? t('dashboard.playWorldFormula1Hint')
                    : t('dashboard.playWorldFootballHint')}
                </span>
              </span>
              <span className="shrink-0 px-3 py-1.5 rounded-full bg-white text-xs font-extrabold text-slate-900">
                {t('dashboard.playWorldGo')}
              </span>
            </button>
          )}
        </div>
      )}

      {showConciergeModal && (
        <OnboardingConciergeModal
          onClose={() => setShowConciergeModal(false)}
          onEnrolled={(gameType, extras) => onWorldEnrolled('', gameType, extras)}
        />
      )}
    </section>
  )
}

export default PublicSection
